import { forwardRef, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import PhoneInput from 'react-phone-number-input/react-hook-form'
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

import { MessageFormPhoneData, MessageFormPhoneProps } from './types'
import { MessageComponent } from './message-component'
import { defineCountryByPhonePrefix } from 'utils/phone'
import { GdprSubmit } from 'components/gdpr-submit'
import { createEvent, restore } from 'effector'
import { resetMessageForm } from './message-form.events'
import { useUnit } from 'effector-react'
import { getGdprSchema, GdprFormValues } from '../gdpr-submit/gdpr-submit'

const doSubmitValues = createEvent<MessageFormPhoneData | null>()
const $submittedValues = restore(doSubmitValues, null).reset(resetMessageForm)

export const MessageFormPhone = forwardRef<
  HTMLDivElement,
  MessageFormPhoneProps
>(({ onSubmit, type, t, phoneCountryCode }, ref) => {
  const data = useUnit($submittedValues)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const schema: yup.ObjectSchema<MessageFormPhoneData & GdprFormValues> =
    getGdprSchema(t).concat(
      yup.object().shape({
        name: yup.string().required(t('welcome.inputs.errors.name')),
        surname: yup.string().required(t('welcome.inputs.errors.surname')),
        phone: yup
          .string()
          .required(t('welcome.inputs.errors.phone.required'))
          .test(
            'isPossiblePhoneNumber',
            t('welcome.inputs.errors.phone.invalid'),
            (value) => (value ? isPossiblePhoneNumber(value, 'RU') : false)
          )
      })
    )

  const formData = useForm<MessageFormPhoneData & GdprFormValues>({
    defaultValues: {
      phone: '+' + phoneCountryCode.replace(/\D/g, '')
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit'
  })

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = formData

  const onSubmitForm = async (data: MessageFormPhoneData) => {
    setIsSubmitting(true)
    await onSubmit(data)
    doSubmitValues(data)
    setIsSubmitting(false)
  }

  if (data) {
    return (
      <MessageComponent
        text={t('welcome.message_bubbles.form_phone.text_after_submit', {
          name: data?.name,
          surname: data?.surname,
          phone: data?.phone
        })}
        type={type}
        ref={ref}
      />
    )
  }

  return (
    <div
      ref={ref}
      className="mx-auto mt-10 max-w-md rounded-[14px] bg-white p-6"
    >
      <h2 className="mb-6 text-left text-[17px] text-gray-800">
        {t('welcome.message_bubbles.form_phone.header')}
      </h2>

      <FormProvider {...formData}>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(onSubmitForm)}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder={t('welcome.inputs.placeholders.name')}
                  className={`w-full rounded-xl border bg-white p-3 ${
                    errors[field.name] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              )}
            />

            <Controller
              name="surname"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder={t('welcome.inputs.placeholders.surname')}
                  className={`w-full rounded-xl border bg-white p-3 ${
                    errors[field.name] ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
              )}
            />
          </div>

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                {...field}
                control={control}
                defaultCountry={defineCountryByPhonePrefix(phoneCountryCode)}
                placeholder={t('welcome.inputs.placeholders.phone')}
                className={`w-full rounded-xl border bg-white p-3 ${
                  errors[field.name] ? 'border-red-500' : 'border-gray-300'
                }`}
                limitMaxLength
                international
              />
            )}
          />

          <GdprSubmit submitting={isSubmitting} />
        </form>
      </FormProvider>
    </div>
  )
})

MessageFormPhone.displayName = 'MessageFormPhone'
